<script lang="ts">
import '@fontsource-variable/space-grotesk';
import '@fontsource-variable/roboto-mono';
import '@fontsource-variable/public-sans';
import '@viamrobotics/prime-core/prime.css';
import '../app.css';
// TODO(mp, 2024-12-3): Remove once Object.groupBy achieves ~95% global usage
// https://caniuse.com/mdn-javascript_builtins_object_groupby
import 'core-js/proposals/array-grouping-v2';

import { setContext, type Snippet } from 'svelte';
import { useResizeObserver } from 'runed';

import {
  NotificationContainer,
  provideNotify,
  provideToast,
  SiteBanner,
  ToastContainer,
} from '@viamrobotics/prime-core';

import { page } from '$app/state';
import { getAgreementsRedirect } from '$lib/agreements';
import {
  provideAPIClients,
  provideUserContext,
  useFeatureFlags,
} from '$lib/api';
import { useClient } from '$lib/api/client';
import { createIsLegalAcceptedQuery } from '$lib/api/end-user';
import { provideOrgsContext, useOrgsContext } from '$lib/api/orgs';
import { prefetchDefaultRegistrySearch } from '$lib/api/search-configuration-blocks';
import { useQueryClient } from '$lib/api/tanstack-query';
import { provideAuthContext, useAuthContext } from '$lib/auth';
import AnnouncementBanner from '$lib/components/announcement-banner.svelte';
import Asidebar from '$lib/components/asidebar/asidebar.svelte';
import Devtools from '$lib/components/devtools.svelte';
import { provideNavigationStateContext } from '$lib/components/link/';
import type { InkeepWidget } from '$lib/inkeep';
import { Nav, PageRoot } from '$lib/layout';
import { navigate } from '$lib/navigate';
import { provideConfigAssistantContext } from '$lib/components/config-assistant/assistant-context';
import AssistedSetupCatalogLoader from '$lib/components/config-assistant/assisted-setup-catalog-loader.svelte';

// PostHog client-side navigation tracking
import { browser } from '$app/environment';
import { beforeNavigate, afterNavigate } from '$app/navigation';
import posthog from 'posthog-js';

if (browser) {
  beforeNavigate(() => posthog.capture('$pageleave'));
  afterNavigate(() => posthog.capture('$pageview'));
}

import type { LayoutData } from './$types';

interface Props {
  children?: Snippet;
  data: LayoutData;
}

const { children, data }: Props = $props();

provideConfigAssistantContext();

provideAuthContext(data.authContext);
provideAPIClients(data.queryClient, data.serviceClients);
provideUserContext();
provideOrgsContext(data.initialOrgID);
provideNotify();
provideToast();
const { currentOrgID } = useOrgsContext();
const queryClient = useQueryClient();
const fleetDashboardClient = useClient('fleetDashboard');
const authContext = useAuthContext();

provideNavigationStateContext(() => {
  const globalQueryParams: Record<string, string> = $currentOrgID
    ? { org: $currentOrgID }
    : {};
  return { globalQueryParams };
});

const isLegalAcceptedQuery = createIsLegalAcceptedQuery();

const authState = $derived(data.authContext.authState);
const agreementsRedirect = $derived(
  getAgreementsRedirect(
    $authState.status,
    $isLegalAcceptedQuery.data?.acceptedLegal,
    page.url.pathname
  )
);

// Once per org (and once for logged-out public browse). Re-runs on org
// switch; TanStack skips the RPC while the default registry query is fresh.
$effect(() => {
  if (!browser) {
    return;
  }
  void prefetchDefaultRegistrySearch(
    queryClient,
    fleetDashboardClient,
    authContext,
    $currentOrgID,
    $authState.status
  );
});

$effect(() => {
  if (agreementsRedirect) {
    void navigate(agreementsRedirect);
  }
});

let sidebarOpen = $state(false);
const closeSidebar = () => {
  sidebarOpen = false;
};
setContext('openSidebar', (inkeep: InkeepWidget) => {
  sidebarOpen = true;
  inkeep?.openModal();
});

const features = useFeatureFlags();
const bannerMessage = $derived($features.ERROR_BANNER);
const announcement = $derived($features.ANNOUNCEMENT_BANNER);

let bannerStackEl = $state<HTMLElement | null>(null);

useResizeObserver(
  () => bannerStackEl,
  ([entry]) => {
    if (!browser) {
      return;
    }
    const height = entry?.contentRect.height ?? 0;
    document.documentElement.style.setProperty(
      '--layout-banner-height',
      `${height}px`
    );
  }
);

$effect(() => {
  if (browser && page.data.skipRootLayout) {
    document.documentElement.style.setProperty('--layout-banner-height', '0px');
  }
});
</script>

{#if page.data.skipRootLayout}
  <div class="flex h-screen flex-col overflow-hidden">
    <PageRoot>
      {@render children?.()}
      <NotificationContainer />
    </PageRoot>
  </div>
{:else}
  <div class="flex h-screen flex-col overflow-hidden">
    <div
      bind:this={bannerStackEl}
      class="w-full shrink-0"
    >
      {#if bannerMessage}
        <SiteBanner value={bannerMessage} />
      {/if}

      <AnnouncementBanner value={announcement} />
    </div>

    <PageRoot>
      {#snippet body()}
        <Nav />
        {@render children?.()}
      {/snippet}
      <Asidebar
        open={sidebarOpen}
        close={closeSidebar}
        {body}
      />
      <NotificationContainer />
    </PageRoot>
  </div>
{/if}

<ToastContainer />
<AssistedSetupCatalogLoader />
<Devtools />
